import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { adaptTemplateBundle } from '../schemas/schemaAdapters'
import { newPendingAssetId, newPendingRenditionId, previewConfiguration, validateBrowserAsset } from '../utils/assets'
import { cloneValue, getAtPath, setAtPath } from '../utils/configPaths'
import { createCropPreview, targetForProfile } from '../utils/imageCrops'
import {
  applyPreset,
  collectionById,
  formatConfiguration,
  mediaReferences,
  withAddedItem,
  withDuplicatedItem,
  withMovedItem,
  withoutItem,
} from '../utils/schemaV2'
import { validateConfiguration } from '../utils/validation'

function formatKey(configuration) {
  return [
    configuration?.format?.id,
    configuration?.format?.width,
    configuration?.format?.height,
  ].join(':')
}

export function useTemplateReview(templateBundle) {
  const template = useMemo(() => adaptTemplateBundle(templateBundle), [templateBundle])
  const [configuration, setConfiguration] = useState(() => cloneValue(template.defaults))
  const [assetUrls, setAssetUrls] = useState({})
  const [assetSourceUrls, setAssetSourceUrls] = useState({})
  const [selectedItems, setSelectedItems] = useState(() => Object.fromEntries(
    (template.schema.collections || []).map((collection) => [collection.id, 0]),
  ))
  const [assetError, setAssetError] = useState(null)
  const previousFormatRef = useRef(formatKey(template.defaults))
  const configurationRef = useRef(configuration)
  configurationRef.current = configuration

  const updateValue = useCallback((path, value) => {
    setConfiguration((current) => {
      let next = setAtPath(current, path, value)
      if (path === 'format.id') {
        const format = (template.manifest.formats || []).find((entry) => entry.id === value)
        if (format) {
          next = setAtPath(next, 'format', formatConfiguration(format))
          const currentPreset = template.presets?.[getAtPath(next, 'presetId')]
          if (currentPreset?.formatId !== value) {
            const reference = (template.manifest.presets || []).find(
              (entry) => template.presets?.[entry.id]?.formatId === value,
            )
            next = reference
              ? applyPreset(next, template.presets[reference.id])
              : setAtPath(next, 'presetId', null)
          }
        }
      } else if (path === 'presetId' && template.presets?.[value]) {
        const preset = template.presets[value]
        const format = (template.manifest.formats || []).find(
          (entry) => entry.id === preset.formatId,
        )
        if (format) next = setAtPath(next, 'format', formatConfiguration(format))
        next = applyPreset(next, preset)
      } else if (path !== 'presetId') {
        const preset = template.presets?.[getAtPath(next, 'presetId')]
        if ((preset?.controlledPaths || []).includes(path)) {
          next = setAtPath(next, 'presetId', null)
        }
      }
      return next
    })
  }, [template])

  const setSelectedItem = useCallback((collectionId, index) => {
    setSelectedItems((current) => ({
      ...current,
      [collectionId]: Math.max(0, Number(index || 0)),
    }))
  }, [])

  const changeCollection = useCallback((collectionId, operation) => {
    const collection = collectionById(template.schema, collectionId)
    if (!collection) return
    setConfiguration((current) => {
      const items = [...getAtPath(current, collection.path, [])]
      const result = operation(items, collection)
      if (!result) return current
      setSelectedItem(collectionId, result.selected)
      return setAtPath(current, collection.path, result.items)
    })
  }, [setSelectedItem, template])

  const addCollectionItem = useCallback((collectionId) => {
    changeCollection(collectionId, withAddedItem)
  }, [changeCollection])

  const removeCollectionItem = useCallback((collectionId, index) => {
    changeCollection(collectionId, (items, collection) => withoutItem(items, collection, index))
  }, [changeCollection])

  const moveCollectionItem = useCallback((collectionId, index, direction) => {
    changeCollection(collectionId, (items) => withMovedItem(items, index, index + direction))
  }, [changeCollection])

  const duplicateCollectionItem = useCallback((collectionId, index) => {
    changeCollection(collectionId, (items, collection) => withDuplicatedItem(items, collection, index))
  }, [changeCollection])

  const addAsset = useCallback((file, options = {}) => {
    const error = validateBrowserAsset(file, options.allowedTypes || null)
    if (error) {
      setAssetError(error)
      return null
    }
    const assetId = newPendingAssetId()
    const reader = new FileReader()
    reader.addEventListener('load', () => {
      if (!options.sourceOnly) {
        setAssetUrls((current) => ({ ...current, [assetId]: reader.result }))
      }
      setAssetSourceUrls((current) => ({ ...current, [assetId]: reader.result }))
    })
    reader.addEventListener('error', () => setAssetError('The image could not be read.'))
    reader.readAsDataURL(file)
    setAssetError(null)
    return assetId
  }, [])

  const addRenditionPreview = useCallback((url) => {
    const id = newPendingRenditionId()
    setAssetUrls((current) => ({ ...current, [id]: url }))
    return id
  }, [])

  useEffect(() => {
    const nextFormat = formatKey(configuration)
    if (previousFormatRef.current === nextFormat) return undefined
    previousFormatRef.current = nextFormat
    const snapshot = configurationRef.current
    let cancelled = false
    Promise.all(mediaReferences(template.schema, snapshot).map(async (reference) => {
      const media = getAtPath(snapshot, reference.path)
      const sourceUrl = assetSourceUrls[media?.sourceAssetId]
      const target = targetForProfile(template.manifest, reference.profile, snapshot)
      if (!media?.sourceAssetId || !sourceUrl || target?.mode !== 'crop') return null
      try {
        const rendered = await createCropPreview(sourceUrl, media.crop, target)
        return { reference, media, rendered }
      } catch {
        return null
      }
    })).then((updates) => {
      if (cancelled) return
      updates.filter(Boolean).forEach(({ reference, media, rendered }) => {
        const renditionAssetId = addRenditionPreview(rendered.previewUrl)
        setConfiguration((current) => setAtPath(current, reference.path, {
          ...media,
          renditionAssetId,
          crop: rendered.crop,
        }))
      })
    })
    return () => { cancelled = true }
  }, [addRenditionPreview, assetSourceUrls, configuration?.format, template])

  const previewSelections = useMemo(() => Object.fromEntries(
    (template.schema.collections || [])
      .filter((collection) => collection.previewSelection)
      .map((collection) => {
        const items = getAtPath(configuration, collection.path, [])
        const index = selectedItems[collection.id] || 0
        return [collection.path, getAtPath(items[index], collection.identityPath, null)]
      })
      .filter(([, id]) => id != null),
  ), [configuration, selectedItems, template])

  const selectPreviewItem = useCallback((collectionPath, itemId) => {
    const collection = (template.schema.collections || []).find(
      (entry) => entry.path === collectionPath,
    )
    if (!collection) return
    const items = getAtPath(configurationRef.current, collection.path, [])
    const index = items.findIndex(
      (item) => String(getAtPath(item, collection.identityPath, '')) === String(itemId),
    )
    if (index >= 0) setSelectedItem(collection.id, index)
  }, [setSelectedItem, template])

  const reset = useCallback(() => {
    setConfiguration(cloneValue(template.defaults))
    setAssetUrls({})
    setAssetSourceUrls({})
    setSelectedItems(Object.fromEntries(
      (template.schema.collections || []).map((collection) => [collection.id, 0]),
    ))
    setAssetError(null)
    previousFormatRef.current = formatKey(template.defaults)
  }, [template])

  const errors = useMemo(
    () => validateConfiguration(template.schema, configuration),
    [configuration, template],
  )

  return {
    template,
    configuration,
    previewConfiguration: previewConfiguration(configuration, assetUrls),
    errors,
    assetError,
    assetUrls,
    assetSourceUrls,
    selectedItems,
    previewSelections,
    updateValue,
    setSelectedItem,
    selectPreviewItem,
    addCollectionItem,
    removeCollectionItem,
    moveCollectionItem,
    duplicateCollectionItem,
    addAsset,
    addRenditionPreview,
    reset,
  }
}
